import { useEffect, useState } from "react";
import { useLenis } from "lenis/react";
import { useSearchParams } from "react-router-dom";
import { Link2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";

const RT_PRICE = 0.005;
const VARIABLE_RATE = 0.00025;

const fixedFee = (v) => (v >= 1000 ? 0.29 : v >= 100 ? 0.19 : 0.09);
const eur = (n, d = 0) => `€${n.toLocaleString("en-GB", { minimumFractionDigits: d, maximumFractionDigits: d })}`;

export default function CostCalculator() {
  const lenis = useLenis();
  const [params] = useSearchParams();
  const [recs, setRecs] = useState(params.get("cc_recs") || "100000");
  const [acceptance, setAcceptance] = useState(params.get("cc_accept") || "60");
  const [avgValue, setAvgValue] = useState(params.get("cc_value") || "45");

  useEffect(() => {
    if (params.get("cc_recs") || params.get("cc_accept") || params.get("cc_value")) {
      const t = setTimeout(() => {
        const el = document.querySelector("[data-testid='cost-calculator']");
        if (el && lenis) {
          lenis.scrollTo(el, { offset: -140, duration: 1.4 });
        } else if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 1500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const r = Math.max(0, Number(recs) || 0);
  const a = Math.min(100, Math.max(0, Number(acceptance) || 0));
  const v = Math.max(0, Number(avgValue) || 0);

  const rtCost = r * RT_PRICE;
  const cdts = r * (a / 100);
  const perCdt = fixedFee(v) + v * VARIABLE_RATE;
  const cdtCost = cdts * perCdt;
  const total = rtCost + cdtCost;
  const perDecision = cdts > 0 ? total / cdts : 0;

  const share = async () => {
    const u = new URL("/", window.location.origin);
    u.searchParams.set("cc_recs", String(r));
    u.searchParams.set("cc_accept", String(a));
    u.searchParams.set("cc_value", String(v));
    u.hash = "pricing";
    try {
      await navigator.clipboard.writeText(u.toString());
      toast.success("Share link copied — it reopens this exact estimate.");
    } catch {
      toast.error("Copy failed — select the URL manually.");
    }
  };

  const field =
    "w-full border border-white/10 bg-[#050505] px-4 py-3 font-mono text-sm text-[#00FF41] outline-none transition-colors duration-300 focus:border-[#00FF41]";
  const label = "font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500";

  return (
    <Reveal delay={0.1} className="mt-24">
      <h3 className="font-display text-3xl font-extrabold tracking-tighter" data-testid="cost-calc-heading">
        Estimate your monthly bill — <span className="text-zinc-600">type your volume, see your number.</span>
      </h3>
      <div className="scanlines mt-10 border border-white/10 bg-[#0F0F11]" data-testid="cost-calculator">
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">axiosera — cost calculator</span>
          <span className="font-mono text-[11px] text-[#00FF41] blink" />
        </div>
        <div className="grid gap-8 p-8 lg:grid-cols-12 lg:p-12">
          <div className="space-y-6 lg:col-span-4">
            <div>
              <label className={label}>Recommendations / month</label>
              <input type="number" min="0" value={recs} onChange={(e) => setRecs(e.target.value)} data-testid="cc-recommendations-input" className={`${field} mt-3`} />
            </div>
            <div>
              <label className={label}>Acceptance rate · %</label>
              <input type="number" min="0" max="100" value={acceptance} onChange={(e) => setAcceptance(e.target.value)} data-testid="cc-acceptance-input" className={`${field} mt-3`} />
            </div>
            <div>
              <label className={label}>Avg applied decision value · €</label>
              <input type="number" min="0" value={avgValue} onChange={(e) => setAvgValue(e.target.value)} data-testid="cc-value-input" className={`${field} mt-3`} />
            </div>
          </div>

          <div className="lg:col-span-8">
            <pre className="font-mono text-xs leading-loose sm:text-sm" data-testid="cc-output">
              <div className="text-zinc-500">
                $ axiosera bill --recommendations {r.toLocaleString()} --accept {a}% --avg-value €{v}
              </div>
              <div className="mt-4 flex justify-between border-b border-white/5 pb-3">
                <span className="text-zinc-500">recommendation transactions · {r.toLocaleString()} × €0.005</span>
                <span className="text-white">{eur(rtCost, 2)}</span>
              </div>
              <div className="flex justify-between border-b border-white/5 py-3">
                <span className="text-zinc-500">applied decisions (CDTs)</span>
                <span className="text-white">{Math.round(cdts).toLocaleString()}</span>
              </div>
              <div className="flex justify-between border-b border-white/5 py-3">
                <span className="text-zinc-500">fee per CDT · {eur(fixedFee(v), 2)} + 0.025% × {eur(v, 2)}</span>
                <span className="text-white">{eur(perCdt, 4)}</span>
              </div>
              <div className="flex justify-between border-b border-white/5 py-3">
                <span className="text-zinc-500">CDT fees</span>
                <span className="text-white">{eur(cdtCost, 2)}</span>
              </div>
              <div className="flex items-baseline justify-between py-4">
                <span className="uppercase tracking-[0.2em] text-zinc-400">Estimated monthly total</span>
                <span className="font-display text-3xl font-extrabold tracking-tighter text-[#00FF41]" data-testid="cc-total-output">
                  {eur(total)}
                </span>
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-600">
                ≈ {eur(perDecision, 4)} per applied decision · no subscriptions, no minimums
              </div>
            </pre>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                data-testid="cc-cta-button"
                onClick={() => lenis?.scrollTo("#pilot", { offset: -72, duration: 1.6 })}
                className="border border-[#00FF41] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#00FF41] transition-colors duration-300 hover:bg-[#00FF41] hover:text-black"
              >
                Validate these numbers in a pilot →
              </button>
              <button
                data-testid="cc-share-button"
                onClick={share}
                className="flex items-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-[#00FF41] hover:text-[#00FF41]"
              >
                <Link2 className="h-4 w-4" /> Copy share link
              </button>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}
